/**
 * PreSubmitConfirmModal — Round 3 lock (Bill 2026-05-12 SPEC Screen 27).
 *
 * Mounted when the FSR clicks "Submit Application" on the Review stage. The
 * modal recaps the cart's posture (applicants / products / deferrals /
 * signing routing) and demands explicit confirmation before the cart
 * transitions out of `draft`. On confirm (S1 punch-list, 2026-06-11):
 *   1. Runs POST /signing/start via ensureSigningSessionForCart — creates the
 *      SigningSession row + binds cart.SigningGroupId, which the commit
 *      pipeline's DisclosurePrefill/SignNowDispatch steps hard-require.
 *      Idempotent (skipped when the cart already has a signingGroupId).
 *   2. Calls the real commit endpoint (`client.retail.carts.commit`) which starts
 *      the live pipeline runner.
 *   3. ALWAYS raises onSubmitted on success — the commit response envelope
 *      does not carry the full cart row, so we synthesize a status-patched
 *      cart; CartDetail re-fetches and mounts CommitFlow / SignStep. (The
 *      previous `if (outcome.cart)` guard never fired — the FSR was stranded
 *      on a completed submit with no visible state change.)
 *
 * Distinct from:
 *   - PipelinePreviewInline — read-only inline preview, no transition
 *   - ContinueConfirmModal — stage-to-stage continue with warnings/deferrals
 *   - CommitFlow's internal ConfirmDialog — re-confirm INSIDE CommitFlow when
 *     the cart is already past `draft` (pipeline-error / retry path)
 *
 * Sources:
 *   - docs/mockups/canonical-walkthrough/SPEC.md Round 3 addendum
 *   - feedback_no_deferring_crap_20260511.md — Submit must be wired
 */
import { useEffect, useState } from 'react';
import type { CartDto, CommitOutcome } from '@retail-ops/shared-types';
import { ApiError, ApiClient } from '@retail-ops/api-client';
import { ModalShell, Button, Banner, Pill, ProcessingOverlay, type ProcessingStep } from '@/components/primitives';
import { useConfig } from '@/contexts/ConfigContext';
import { useAuth } from '@/contexts/AuthContext';
import { ensureSigningSessionForCart, type SigningPrepareProgress } from '../services/signingSetup';
import { useNavigate } from 'react-router';
import { DeclineApplicationModal } from './DeclineApplicationModal';

export interface PreSubmitConfirmSummary {
  /** Plain-text applicant tally e.g. "1 Personal, 1 Joint". */
  applicantsLine: string;
  /** Plain-text product tally e.g. "Free Checking + Premier Money Market". */
  productsLine: string;
  /** Count of warnings the FSR is implicitly acknowledging on submit. */
  warningCount: number;
  /** Count of deferred gates tracked post-AcctAdd. */
  deferralCount: number;
}

export interface PreSubmitConfirmModalProps {
  open: boolean;
  cart: CartDto;
  client: ApiClient;
  summary: PreSubmitConfirmSummary;
  onCancel: () => void;
  /** Fired after a successful submit so the parent can refresh cart state. */
  onSubmitted: (cart: CartDto) => void;
  /** AbortController signal factory from the calling hook. */
  makeSignal: () => AbortSignal;
}

export function PreSubmitConfirmModal({
  open,
  cart,
  client,
  summary,
  onCancel,
  onSubmitted,
  makeSignal,
}: PreSubmitConfirmModalProps): JSX.Element | null {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [phase, setPhase] = useState<'idle' | 'signing-setup' | 'committing'>('idle');
  const [error, setError] = useState<string | null>(null);
  // REQ-167 phase 2 — live per-document build progress from the async
  // prepare job ("Preparing document 3 of 7 · ~12s left").
  const [prepareProgress, setPrepareProgress] = useState<SigningPrepareProgress | null>(null);
  const [beganAt, setBeganAt] = useState<number | null>(null);
  const { config } = useConfig();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showDecline, setShowDecline] = useState(false);
  // A required gate stopped the commit (Bill's smoke test 2026-10-29): Continue stayed live and the
  // Decline lived back on Review. Now Continue is off and the decline is offered here. The server
  // refuses the commit either way; this only stops the officer pressing a dead button.
  const hardStopped = !!error && error.includes('strategy=required-hard-stop');

  const onConfirm = async (): Promise<void> => {
    setSubmitting(true);
    setBeganAt(Date.now());
    setError(null);
    setPrepareProgress(null);
    try {
      // Leg 1 — S1.2 signing prerequisite: SigningSession + SigningGroupId.
      setPhase('signing-setup');
      await ensureSigningSessionForCart({
        cart,
        client,
        config: {
          disclosures: config.disclosures,
          entityRules: config.entityRules,
          entityRequiredDocs: config.entityRequiredDocs,
          products: config.products,
        },
        ...(user?.upn ? { fsrEmail: user.upn } : {}),
        ...(user?.displayName ? { fsrName: user.displayName } : {}),
        signal: makeSignal(),
        onProgress: setPrepareProgress,
      });

      // Leg 2 — the commit pipeline. NO abort signal: an unmount-scoped signal
      // cancelled the pipeline mid-Core-write when the status poll swapped
      // panels (2026-07-02 incident). State-changing banking POSTs never
      // carry unmount-abort signals.
      setPhase('committing');
      const outcome: CommitOutcome = await client.retail.carts.commit(cart.cartId);
      if (!outcome.success) {
        setError(outcome.error ?? 'Submit could not start. Resolve the blockers and try again.');
        return;
      }
      // Always fire — the response envelope has no top-level cart row, so we
      // synthesize a status-patched cart and let the parent re-fetch truth
      // (typically 'awaiting-signatures' after the pipeline's step-6 pause).
      onSubmitted(outcome.cart ?? { ...cart, status: 'committing' });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      const message = ApiError.isApiError(err)
        ? err.message
        : err instanceof Error
          ? err.message
          : 'Unexpected error during submit.';
      setError(message);
    } finally {
      setSubmitting(false);
      setPhase('idle');
      setPrepareProgress(null);
      setBeganAt(null);
    }
  };

  // BACKLOG-1672 (Bill 2026-10-02): "Referring to a signing session when there may
  // not be one … it should be building disclosures, determining signature
  // requirements, etc." Ask the server up front which documents need a signature
  // so the modal, the button and the overlay describe THIS application.
  const [sigSummary, setSigSummary] = useState<{ docs: string[]; signers: number } | null>(null);
  useEffect(() => {
    if (!open) { setSigSummary(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await client.retail.disclosures.resolve({ cartId: cart.cartId });
        if (cancelled) return;
        const rows = res.resolved.filter((r) => r.requiresSignature);
        const roles = new Set<string>();
        rows.forEach((r) => (r.signerRoles ?? []).forEach((x) => roles.add(x.toLowerCase())));
        setSigSummary({ docs: rows.map((r) => r.title || r.templateCode), signers: roles.size });
      } catch {
        if (!cancelled) setSigSummary(null); // unknown → neutral wording below
      }
    })();
    return () => { cancelled = true; };
  }, [open, cart.cartId, client]);
  const nothingToSign = sigSummary !== null && sigSummary.docs.length === 0;

  // Blocking overlay steps (Bill 2026-09-30) — plain banking language.
  const overlaySteps: ProcessingStep[] = (() => {
    const preparing = phase === 'signing-setup' && prepareProgress?.phase === 'preparing';
    const starting = phase === 'signing-setup' && prepareProgress?.phase === 'starting';
    const docsDetail = preparing && prepareProgress?.currentDoc !== undefined && prepareProgress?.totalDocs
      ? `document ${Math.min(prepareProgress.currentDoc + 1, prepareProgress.totalDocs)} of ${prepareProgress.totalDocs}`
      : undefined;
    return [
      { key: 'session', label: 'Creating or updating the customer records in Core Director', state: phase === 'signing-setup' && !preparing && !starting ? 'active' : phase === 'idle' ? 'pending' : 'done' },
      { key: 'render', label: 'Reserving account numbers and building the disclosure documents', state: preparing ? 'active' : starting || phase === 'committing' ? 'done' : 'pending', ...(docsDetail ? { detail: docsDetail } : {}) },
      { key: 'bind', label: nothingToSign ? 'Confirming no signatures are required' : 'Determining who signs which documents', state: starting ? 'active' : phase === 'committing' ? 'done' : 'pending' },
      { key: 'commit', label: 'Finishing the application in Core Director', state: phase === 'committing' ? 'active' : 'pending' },
    ];
  })();
  const overlayProgress = phase === 'signing-setup' && prepareProgress?.phase === 'preparing' && prepareProgress.totalDocs
    ? { done: Math.min(prepareProgress.currentDoc ?? 0, prepareProgress.totalDocs), total: prepareProgress.totalDocs }
    : null;

  if (!open) return null;

  const totalAck = summary.warningCount + summary.deferralCount;

  // Progress-aware busy label (REQ-167 phase 2).
  const busyLabel = ((): string => {
    if (phase === 'committing') return 'Finishing in Core Director…';
    if (prepareProgress?.phase === 'starting') return nothingToSign ? 'Confirming signature requirements…' : 'Determining who signs which documents…';
    if (prepareProgress?.phase === 'preparing' && prepareProgress.currentDoc && prepareProgress.totalDocs) {
      const eta = prepareProgress.estimatedRemainingMs;
      const etaTxt = typeof eta === 'number' && eta > 0 ? ` · ~${Math.max(1, Math.ceil(eta / 1000))}s left` : '';
      return `Building document ${Math.min(prepareProgress.currentDoc + 1, prepareProgress.totalDocs)} of ${prepareProgress.totalDocs}${etaTxt}`;
    }
    return 'Building disclosure documents…';
  })();

  const overlay = (
    <ProcessingOverlay
      open={submitting}
      title="Submitting for account opening"
      message={busyLabel}
      steps={overlaySteps}
      progress={overlayProgress}
      startedAt={beganAt}
    />
  );

  return (<>
    {overlay}
    <ModalShell
      open={open}
      onClose={() => { if (!submitting) onCancel(); }}
      title="Cart Approved"
      size="md"
      disableBackdropClose={submitting}
      disableEscapeClose={submitting}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>
            ← Back to Review
          </Button>
          {hardStopped && (
            <Button variant="danger" onClick={() => setShowDecline(true)} disabled={submitting}>
              Decline application
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => { void onConfirm(); }}
            disabled={submitting || hardStopped}
          >
            {submitting ? busyLabel : nothingToSign ? 'Continue — nothing to sign →' : sigSummary ? 'Continue to signing →' : 'Continue →'}
          </Button>
        </>
      }
    >
      <div className="rao-presubmit">
        {/* Bill 2026-07-13 Item 19 — exact approved wording. */}
        <p className="rao-presubmit__intro">
          Ready to create or update the customer records in Core Director, reserve
          the account numbers, and then build the disclosure package with those
          numbers on it (BACKLOG-1674).
          {sigSummary === null && ' The next step determines which documents, if any, need a signature.'}
          {nothingToSign && ' Nothing on this application requires a signature: the disclosures are delivered and the account opening continues straight to commit.'}
          {sigSummary && sigSummary.docs.length > 0 && (
            <> {sigSummary.docs.length === 1 ? 'One document needs a signature' : `${sigSummary.docs.length} documents need signatures`}
              {sigSummary.signers > 0 ? ` from ${sigSummary.signers === 1 ? 'one person' : `${sigSummary.signers} people`}` : ''}
              {': '}<strong>{sigSummary.docs.join(', ')}</strong>. Next you will mark who is here to sign now; anyone not present gets a remote signing link and a follow-up task on you until they sign. The account opening does not wait.
            </>
          )}
          {' '}The cart locks once documents are built — if anything needs to change after this point, use <em>Regenerate documents</em>.
        </p>

        <dl className="rao-presubmit__recap">
          <div>
            <dt>Applicants</dt>
            <dd>{summary.applicantsLine}</dd>
          </div>
          <div>
            <dt>Products</dt>
            <dd>{summary.productsLine}</dd>
          </div>
          <div>
            <dt>Officer</dt>
            <dd><strong>{cart.officerCode}</strong> · Branch {cart.branch}</dd>
          </div>
        </dl>

        {totalAck > 0 && (
          <div className="rao-presubmit__ack">
            <strong>Acknowledging on submit:</strong>{' '}
            {summary.warningCount > 0 && (
              <Pill variant="warn">
                {summary.warningCount} warning{summary.warningCount === 1 ? '' : 's'}
              </Pill>
            )}
            {summary.deferralCount > 0 && (
              <Pill variant="err">
                {summary.deferralCount} deferral{summary.deferralCount === 1 ? '' : 's'}
              </Pill>
            )}
          </div>
        )}

        {/* REQ-167 phase 2 — live disclosure-build progress line. Mirrors the
            button label so the FSR sees motion even on long packages. */}
        {submitting && phase === 'signing-setup' && (
          <p aria-live="polite" style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 'var(--sp-3)' }}>
            {busyLabel}
          </p>
        )}

        {error && (
          <Banner tone="err">{error}</Banner>
        )}
        {hardStopped && (
          <Banner tone="info">
            A required check stopped this application, so it can't be opened. Choose <strong>Decline application</strong> to
            record the decline (Adverse Action sends the adverse-action letter per procedure), or return to Review.
          </Banner>
        )}
      </div>
    </ModalShell>
    <DeclineApplicationModal
      open={showDecline}
      cartId={cart.cartId}
      initialReason="Adverse"
      onClose={() => setShowDecline(false)}
      onDeclined={() => { setShowDecline(false); navigate('/rao'); }}
      {...(cart.officerCode ? { officerCode: cart.officerCode } : {})}
    />
  </>);
}
